import type { EditorContext, EditorCore, EditorRegion, IconRef } from "@loumy/contracts";
import { ALL_EDITOR_CORES } from "@loumy/editors";
import type { ComponentType } from "react";
import type { Menu } from "../editor/MenuBar";

// D66 Frontend-Editor-Registry. „Nichts referenziert einen Editor hart": die EditorShell
// und die Editor-Auswahl gehen ausschließlich hierdurch. EditorCore EAGER (Listing/Defaults/
// documentSchema), EditorUI LAZY (`() => import('../editor/editors/<id>/ui')`) — ein Editor
// = ein Ordner + eine Zeile in EDITOR_UI_LOADERS (§5-Analogon). Spiegelt toolRegistry.ts.

/**
 * Props jeder Region-Komponente. Der Context trägt lose Typen (doc/ui = unknown); der
 * jeweilige Editor castet auf seine TDoc/TUi (analog SettingsFormProps beim Tool) — ein
 * einheitlicher Props-Typ = keine Generics-/Varianz-Reibung im Registry-Store.
 */
export interface WebEditorPanelProps {
  ctx: EditorContext;
}

export interface WebPanelDescriptor {
  readonly id: string;
  readonly region: EditorRegion;
  readonly title: string;
  readonly icon?: IconRef;
  readonly Component: ComponentType<WebEditorPanelProps>;
}

/** Web-EditorUI: echte React-Komponenten über dem D66-Vertrag (EditorUI). */
export interface WebEditorUI {
  readonly id: string;
  readonly panels: readonly WebPanelDescriptor[];
  /**
   * Editor-spezifische Menüs (Bearbeiten/Ansicht/„Datei"-Editor-Einträge wie Export), die die Shell in
   * die GETEILTE Menüleiste unter den Tabs einbettet (D66/D87). Die Shell steuert die geteilten Datei-
   * (Projekt-Quellen) und Level-Menüs (Multi-Doc-Host, D72) bei und merged beides per Label. So bleibt
   * „Menüs editor-spezifisch, Auswahl/Projekt Shell" (D66) gewahrt, ohne eine zweite Menüleiste.
   */
  readonly menus?: (ctx: EditorContext) => Menu[];
}

type UiModule = { default: WebEditorUI };
type UiLoader = () => Promise<UiModule>;

// Die EINE Stelle, an der Editoren angedockt werden (§5-Analogon). Reihenfolge = Auswahl-Order.
const EDITOR_UI_LOADERS: Record<string, UiLoader> = {
  "demo-board": () => import("../editor/editors/demo-board/ui"),
  "platformer-2d": () => import("../editor/editors/platformer-2d/ui"),
};

const coresById = new Map<string, EditorCore>(ALL_EDITOR_CORES.map((c) => [c.id, c]));
const uiCache = new Map<string, Promise<WebEditorUI | undefined>>();

/** Alle EditorCores in stabiler Insertion-Order (Editor-Auswahl, §11). */
export function listEditors(): EditorCore[] {
  return [...coresById.values()];
}

export function getEditorCore(id: string): EditorCore | undefined {
  return coresById.get(id);
}

/** Lazy EditorUI laden (gecacht). Kein registrierter Loader → undefined (leere Shell). */
export function loadEditorUI(id: string): Promise<WebEditorUI | undefined> {
  const cached = uiCache.get(id);
  if (cached) return cached;
  const loader = EDITOR_UI_LOADERS[id];
  const p: Promise<WebEditorUI | undefined> = loader ? loader().then((m) => m.default) : Promise.resolve(undefined);
  uiCache.set(id, p);
  return p;
}
